/**
 * Accessory Search — full search results page controller.
 *
 * Powers the TilbehørSøk (Accessory Search) page via the <accessory-search>
 * custom element. No vehicle integration — pure accessory filtering.
 */

const AS_API_BASE_FALLBACK = 'https://static.218.250.90.157.clients.your-server.de';

class AccessorySearchElement extends HTMLElement {
  connectedCallback() {
    this._api = this.dataset.apiBase || AS_API_BASE_FALLBACK;
    this._pageSize = parseInt(this.dataset.pageSize, 10) || 24;
    this._defaultCategory = this.dataset.defaultCategory || '';
    this._stateKey = `fh_as_state:${window.location.pathname}`;

    // DOM refs
    this._searchInput = this.querySelector('[data-role="search-input"]');
    this._sortSelect = this.querySelector('[data-role="sort"]');
    this._sidebar = this.querySelector('[data-role="sidebar"]');
    this._sidebarCount = this.querySelector('[data-role="sidebar-count"]');
    this._filterToggle = this.querySelector('[data-role="filter-toggle"]');
    this._filterClose = this.querySelector('[data-role="filter-close"]');
    this._filterBackdrop = this.querySelector('[data-role="filter-backdrop"]');
    this._filterResets = this.querySelectorAll('[data-role="filter-reset"]');
    this._activeFiltersEl = this.querySelector('[data-role="active-filters"]');
    this._grid = this.querySelector('[data-role="grid"]');
    this._loading = this.querySelector('[data-role="loading"]');
    this._empty = this.querySelector('[data-role="empty"]');
    this._countEl = this.querySelector('[data-role="count"]');
    this._pagination = this.querySelector('[data-role="pagination"]');

    // Accordion elements — keyed by data-accordion or data-slider-accordion value
    this._accordions = {};
    for (const el of this.querySelectorAll('[data-accordion]')) {
      this._accordions[el.dataset.accordion] = el;
    }
    for (const el of this.querySelectorAll('[data-slider-accordion]')) {
      this._accordions[el.dataset.sliderAccordion] = el;
    }

    // Price slider
    this._sliders = {};
    const priceSliderEl = this.querySelector('[data-slider="price"]');
    if (priceSliderEl) {
      this._sliders.price = {
        container: priceSliderEl,
        thumbMin: priceSliderEl.querySelector('[data-role="thumb-min"]'),
        thumbMax: priceSliderEl.querySelector('[data-role="thumb-max"]'),
        fill: priceSliderEl.querySelector('[data-role="slider-fill"]'),
        valMin: priceSliderEl.querySelector('[data-role="slider-min"]'),
        valMax: priceSliderEl.querySelector('[data-role="slider-max"]'),
      };
    }

    // Stock checkbox
    this._stockCheckbox = this.querySelector('[data-filter="in_stock"]');

    // State
    this._page = 1;
    this._filters = {};
    this._sort = '';
    this._query = '';
    this._searchDebounce = null;

    // Price slider state
    this._priceAbsMin = 0;
    this._priceAbsMax = 15000;
    this._priceMin = this._priceAbsMin;
    this._priceMax = this._priceAbsMax;

    // Set up event listeners
    this._bindSearchInput();
    this._bindSearchButton();
    this._bindSort();
    this._bindAccordionToggles();
    this._bindMobileFilter();
    this._bindReset();
    this._bindStockFilter();
    this._initPriceSlider();

    // Restore state from URL params
    this._readUrlParams();

    // Apply default category if no category filter set from URL
    if (this._defaultCategory && !this._filters.category) {
      this._filters.category = this._defaultCategory;
    }

    // Initial fetch
    this._fetchResults();
  }

  // ── Initialization helpers ────────────────────────────────────────────

  _readUrlParams() {
    this._filters = {};
    const params = new URLSearchParams(window.location.search);

    // Check if URL has any filter params
    let hasUrlParams = false;
    for (const key of params.keys()) {
      if (key !== 'page' && key !== 'sort' && key !== 'q') {
        hasUrlParams = true;
        break;
      }
    }

    const hasSearchQuery = Boolean(params.get('q'));
    const canRestoreSessionState = !hasUrlParams && !hasSearchQuery && !this._defaultCategory;

    if (canRestoreSessionState) {
      try {
        const stateStr = sessionStorage.getItem(this._stateKey);
        if (stateStr) {
          const state = JSON.parse(stateStr);
          if (state.filters) this._filters = state.filters;
          if (state.query) this._query = state.query;
          if (state.sort) this._sort = state.sort;
          if (state.page) this._page = state.page;
          if (state.priceMin) this._priceMin = state.priceMin;
          if (state.priceMax) this._priceMax = state.priceMax;

          if (this._searchInput && this._query) this._searchInput.value = this._query;
          if (this._sortSelect && this._sort) this._sortSelect.value = this._sort;
          if (this._stockCheckbox && this._filters.in_stock) this._stockCheckbox.checked = true;
        }
      } catch (e) { console.warn('Failed to restore filter state', e); }
    }

    const q = params.get('q');
    if (q) {
      this._query = q;
      if (this._searchInput) this._searchInput.value = q;
    }

    // Accordion-backed filters
    const facetParams = ['category', 'brand', 'color', 'pcd', 'size', 'center_bore', 'thickness', 'thread_size'];
    for (const key of facetParams) {
      const val = params.get(key);
      if (val) this._filters[key] = val;
    }

    // Price
    const minPrice = params.get('min_price');
    const maxPrice = params.get('max_price');
    if (minPrice) {
      this._filters.min_price = parseInt(minPrice, 10);
      this._priceMin = parseInt(minPrice, 10);
    }
    if (maxPrice) {
      this._filters.max_price = parseInt(maxPrice, 10);
      this._priceMax = parseInt(maxPrice, 10);
    }

    // Stock
    if (params.get('in_stock') === 'true' || params.get('in_stock') === '1') {
      this._filters.in_stock = 'true';
      if (this._stockCheckbox) this._stockCheckbox.checked = true;
    }

    // Sort
    const sort = params.get('sort');
    if (sort) {
      this._sort = sort;
      if (this._sortSelect) this._sortSelect.value = sort;
    }

    // Page
    const page = parseInt(params.get('page'), 10);
    if (page > 0) this._page = page;
  }

  _bindSearchInput() {
    if (!this._searchInput) return;

    this._searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        clearTimeout(this._searchDebounce);
        this._query = this._searchInput.value.trim();
        this._page = 1;
        this._fetchResults();
      }
    });

    this._searchInput.addEventListener('input', () => {
      clearTimeout(this._searchDebounce);
      this._searchDebounce = setTimeout(() => {
        this._query = this._searchInput.value.trim();
        this._page = 1;
        this._fetchResults();
      }, 300);
    });
  }

  _bindSort() {
    if (!this._sortSelect) return;
    this._sortSelect.addEventListener('change', () => {
      this._sort = this._sortSelect.value;
      this._page = 1;
      this._fetchResults();
    });
  }

  _bindAccordionToggles() {
    // Wire up expand/collapse toggles for each accordion container
    for (const acc of Object.values(this._accordions)) {
      const toggle = acc.querySelector('.as__accordion-toggle');
      const body = acc.querySelector('.as__accordion-body');
      const chevron = acc.querySelector('.as__accordion-chevron');
      if (!toggle || !body) continue;

      toggle.addEventListener('click', () => {
        const collapsed = body.classList.toggle('is-collapsed');
        if (chevron) chevron.classList.toggle('is-rotated', collapsed);
      });
    }
  }

  _bindMobileFilter() {
    const open = () => {
      if (this._sidebar) this._sidebar.classList.add('as__sidebar--open');
      if (this._filterBackdrop) this._filterBackdrop.hidden = false;
    };
    const close = () => {
      if (this._sidebar) this._sidebar.classList.remove('as__sidebar--open');
      if (this._filterBackdrop) this._filterBackdrop.hidden = true;
    };

    this._filterToggle?.addEventListener('click', open);
    this._filterClose?.addEventListener('click', close);
    this._filterBackdrop?.addEventListener('click', close);
  }

  _bindSearchButton() {
    const btn = this.querySelector('[data-role="search-btn"]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      clearTimeout(this._searchDebounce);
      this._query = this._searchInput ? this._searchInput.value.trim() : '';
      this._page = 1;
      this._fetchResults();
    });
  }

  _bindReset() {
    if (!this._filterResets || this._filterResets.length === 0) return;
    for (const btn of this._filterResets) {
      btn.addEventListener('click', () => this._resetFilters());
    }
  }

  _bindStockFilter() {
    if (!this._stockCheckbox) return;
    this._stockCheckbox.addEventListener('change', () => {
      if (this._stockCheckbox.checked) {
        this._filters.in_stock = 'true';
      } else {
        delete this._filters.in_stock;
      }
      this._page = 1;
      this._updateFilterCount();
      this._fetchResults();
    });
  }

  // ── Price slider ──────────────────────────────────────────────────────

  _initPriceSlider() {
    const sl = this._sliders.price;
    if (!sl || !sl.thumbMin || !sl.thumbMax || !sl.fill || !sl.valMin || !sl.valMax) return;

    const track = sl.container.querySelector('.as__slider-track');
    if (!track) return;

    const updateSliderUI = () => {
      const range = this._priceAbsMax - this._priceAbsMin;
      if (range <= 0) return;
      const minPct = ((this._priceMin - this._priceAbsMin) / range) * 100;
      const maxPct = ((this._priceMax - this._priceAbsMin) / range) * 100;
      sl.fill.style.left = minPct + '%';
      sl.fill.style.width = (maxPct - minPct) + '%';
      sl.thumbMin.style.left = minPct + '%';
      sl.thumbMax.style.left = maxPct + '%';
      sl.valMin.textContent = Math.round(this._priceMin) + ' kr';
      sl.valMax.textContent = Math.round(this._priceMax) + ' kr';
    };

    const startDrag = (thumb, isMin) => {
      const onMove = (e) => {
        const rect = track.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        let pct = (clientX - rect.left) / rect.width;
        pct = Math.max(0, Math.min(1, pct));
        const val = this._priceAbsMin + pct * (this._priceAbsMax - this._priceAbsMin);
        if (isMin) {
          this._priceMin = Math.min(val, this._priceMax - 100);
        } else {
          this._priceMax = Math.max(val, this._priceMin + 100);
        }
        updateSliderUI();
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onUp);
        this._applyPriceFilter();
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove, { passive: true });
      document.addEventListener('touchend', onUp);
    };

    sl.thumbMin.addEventListener('mousedown', (e) => { e.preventDefault(); startDrag(sl.thumbMin, true); });
    sl.thumbMin.addEventListener('touchstart', () => { startDrag(sl.thumbMin, true); }, { passive: true });
    sl.thumbMax.addEventListener('mousedown', (e) => { e.preventDefault(); startDrag(sl.thumbMax, false); });
    sl.thumbMax.addEventListener('touchstart', () => { startDrag(sl.thumbMax, false); }, { passive: true });

    this._priceMin = this._priceAbsMin;
    this._priceMax = this._priceAbsMax;
    updateSliderUI();
    this._updatePriceSliderUI = updateSliderUI;
  }

  _applyPriceFilter() {
    if (this._priceMin > this._priceAbsMin) {
      this._filters.min_price = Math.round(this._priceMin);
    } else {
      delete this._filters.min_price;
    }
    if (this._priceMax < this._priceAbsMax) {
      this._filters.max_price = Math.round(this._priceMax);
    } else {
      delete this._filters.max_price;
    }
    this._page = 1;
    this._updateFilterCount();
    this._fetchResults();
  }

  // ── API fetch ─────────────────────────────────────────────────────────

  async _fetchResults() {
    this._showLoading(true);

    const params = new URLSearchParams();
    params.set('page', this._page);
    params.set('limit', this._pageSize);
    if (this._query) params.set('q', this._query);
    if (this._sort) params.set('sort', this._sort);

    // Add all active filters
    for (const [key, val] of Object.entries(this._filters)) {
      if (val !== undefined && val !== null && val !== '') {
        params.set(key, val);
      }
    }

    // Save state to sessionStorage
    try {
      const stateToSave = {
        filters: this._filters,
        query: this._query,
        sort: this._sort,
        page: this._page,
        priceMin: this._priceMin,
        priceMax: this._priceMax
      };
      sessionStorage.setItem(this._stateKey, JSON.stringify(stateToSave));
    } catch (e) { }

    try {
      const resp = await fetch(`${this._api}/api/v2/accessories?${params}`);
      if (!resp.ok) throw new Error(`API ${resp.status}`);
      const json = await resp.json();
      if (json.success) {
        this._renderResults(json.data || []);
        this._renderFacets(json.facets || {});
        this._renderActiveFilters();
        this._renderPagination(json.pagination || {});
        this._updateCount(json.pagination?.total ?? 0);
        this._updateFilterCount();
        this._updateUrl(params);
      } else {
        throw new Error('API returned success: false');
      }
    } catch (err) {
      console.error('[accessory-search] Fetch error:', err);
      this._showLoading(false);
      if (this._grid) this._grid.innerHTML = '';
      if (this._empty) {
        this._empty.hidden = false;
        const msg = this._empty.querySelector('p');
        if (msg) msg.textContent = 'Noe gikk galt. Pr\u00f8v igjen senere.';
      }
      if (this._pagination) this._pagination.innerHTML = '';
    } finally {
      this._showLoading(false);
    }
  }

  _updateUrl(params) {
    // Mirror active state into the URL for shareable links / browser back
    const urlParams = new URLSearchParams();
    if (this._query) urlParams.set('q', this._query);
    if (this._sort) urlParams.set('sort', this._sort);
    if (this._page > 1) urlParams.set('page', String(this._page));
    for (const [key, val] of Object.entries(this._filters)) {
      if (val !== undefined && val !== null && val !== '') {
        urlParams.set(key, val);
      }
    }
    const qs = urlParams.toString();
    window.history.replaceState(
      {},
      '',
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    );
  }

  // ── Render: product grid ──────────────────────────────────────────────

  _renderResults(data) {
    if (!this._grid) return;

    if (!data.length) {
      this._grid.innerHTML = '';
      if (this._empty) this._empty.hidden = false;
      return;
    }

    if (this._empty) this._empty.hidden = true;

    this._grid.innerHTML = data.map((p) => {
      const price = p.retail_price_nok != null
        ? `${p.retail_price_nok.toLocaleString('nb-NO')} kr`
        : '';
      const stockClass = p.stock > 0 ? 'as__card-stock--order' : 'as__card-stock--out';
      const stockText = p.stock > 0 ? 'Bestillingsvare' : 'Ikke p\u00e5 lager';
      const imgHtml = p.main_image
        ? `<img class="as__card-img" src="${this._esc(p.main_image)}" alt="${this._esc(p.name || '')}" loading="lazy">`
        : `<span class="as__card-img-placeholder">Ingen bilde</span>`;
      const href = p.shopify_handle ? `/products/${this._esc(p.shopify_handle)}` : '#';

      return `
        <div class="as__card">
          <a href="${href}">
            <div class="as__card-img-wrap">${imgHtml}</div>
            <div class="as__card-body">
              <div class="as__card-brand">${this._esc(p.brand || '')}</div>
              <div class="as__card-name">${this._esc(p.name || '')}</div>
              ${p.category ? `<div class="as__card-category">${this._esc(p.category)}</div>` : ''}
              <div class="as__card-footer">
                <div class="as__card-price">${price}</div>
                <div class="as__card-stock ${stockClass}">${stockText}</div>
              </div>
            </div>
          </a>
        </div>
      `;
    }).join('');
  }

  // ── Render: facet accordions ──────────────────────────────────────────

  _renderFacets(facets) {
    // Map between the accordion data-accordion attribute key and the API facet key,
    // and the filter param key sent to the API.
    // accordion key  → { facetKey: key in facets response, filterKey: key sent to API }
    const accordionDefs = [
      { acc: 'category', facetKey: 'category', filterKey: 'category' },
      { acc: 'brand', facetKey: 'brand', filterKey: 'brand' },
      { acc: 'color', facetKey: 'colour', filterKey: 'color' },
      { acc: 'pcd', facetKey: 'pcd', filterKey: 'pcd' },
      { acc: 'size', facetKey: 'size', filterKey: 'size' },
      { acc: 'center_bore', facetKey: 'center_bore', filterKey: 'center_bore' },
      { acc: 'thickness', facetKey: 'thickness', filterKey: 'thickness' },
      { acc: 'thread_size', facetKey: 'thread_size', filterKey: 'thread_size' },
    ];

    for (const { acc, facetKey, filterKey } of accordionDefs) {
      const accordion = this._accordions[acc];
      if (!accordion) continue;

      const items = facets[facetKey] || [];
      const listEl = accordion.querySelector('[data-role="checkbox-list"]');
      if (!listEl) continue;

      if (items.length === 0) {
        accordion.hidden = true;
        continue;
      }
      accordion.hidden = false;

      // Preserve checked state across re-renders
      const checkedSet = new Set(
        (this._filters[filterKey] || '').split(',').filter(Boolean)
      );

      listEl.innerHTML = items.map((item) => {
        const val = String(item.value);
        const checked = checkedSet.has(val) ? 'checked' : '';
        return `
          <label class="as__checkbox-item">
            <input type="checkbox" value="${this._esc(val)}" ${checked}>
            <span class="as__checkbox-name">${this._esc(val)}</span>
            <span class="as__facet-count">${item.count}</span>
          </label>
        `;
      }).join('');

      // Attach change listeners to freshly rendered checkboxes
      for (const cb of listEl.querySelectorAll('input[type="checkbox"]')) {
        cb.addEventListener('change', () => {
          const checked = [...listEl.querySelectorAll('input:checked')].map((c) => c.value);
          if (checked.length > 0) {
            this._filters[filterKey] = checked.join(',');
          } else {
            delete this._filters[filterKey];
          }
          this._page = 1;
          this._updateFilterCount();
          this._fetchResults();
        });
      }
    }
  }

  // ── Render: pagination ────────────────────────────────────────────────

  _renderPagination({ page, total_pages } = {}) {
    if (!this._pagination) return;

    if (!total_pages || total_pages <= 1) {
      this._pagination.innerHTML = '';
      return;
    }

    const pages = [];
    const range = 2;

    if (page > 1) pages.push({ label: '\u2039', value: page - 1 });

    for (let i = 1; i <= total_pages; i++) {
      if (i === 1 || i === total_pages || (i >= page - range && i <= page + range)) {
        pages.push({ label: String(i), value: i, active: i === page });
      } else if (pages.length > 0 && pages[pages.length - 1].label !== '\u2026') {
        pages.push({ label: '\u2026', value: null });
      }
    }

    if (page < total_pages) pages.push({ label: '\u203a', value: page + 1 });

    this._pagination.innerHTML = pages.map((p) => {
      if (p.value === null) {
        return `<span class="as__page-btn as__page-btn--dots">\u2026</span>`;
      }
      const cls = p.active ? 'as__page-btn as__page-btn--active' : 'as__page-btn';
      return `<button class="${cls}" data-page="${p.value}">${p.label}</button>`;
    }).join('');

    for (const btn of this._pagination.querySelectorAll('[data-page]')) {
      btn.addEventListener('click', () => {
        this._page = parseInt(btn.dataset.page, 10);
        this._fetchResults();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  // ── UI helpers ────────────────────────────────────────────────────────

  _showLoading(show) {
    if (this._loading) this._loading.hidden = !show;
    if (this._grid) this._grid.style.visibility = show ? 'hidden' : 'visible';
  }

  _updateCount(total) {
    if (!this._countEl) return;
    this._countEl.textContent = `Viser ${total.toLocaleString('nb-NO')} tilbeh\u00f8r`;
  }

  _updateFilterCount() {
    if (!this._sidebarCount) return;

    let count = 0;

    // Count accordion-backed filters (each comma-separated value is one active filter)
    const filterKeys = ['category', 'brand', 'color', 'pcd', 'size', 'center_bore', 'thickness', 'thread_size'];
    for (const key of filterKeys) {
      if (this._filters[key]) {
        count += this._filters[key].split(',').filter(Boolean).length;
      }
    }

    // Stock checkbox
    if (this._filters.in_stock) count += 1;

    // Price range
    if (this._filters.min_price) count += 1;
    if (this._filters.max_price) count += 1;

    this._sidebarCount.textContent = count > 0 ? String(count) : '';
    this._sidebarCount.hidden = count === 0;
  }

  _renderActiveFilters() {
    if (!this._activeFiltersEl) return;

    const labels = {
      category: 'Kategori',
      brand: 'Merke',
      color: 'Farge',
      center_bore: 'Ytre Ø',
      size: 'Indre Ø',
      thickness: 'Tykkelse',
      pcd: 'Boltsirkel',
      thread_size: 'Gjenge',
    };

    const chips = [];

    // Multi-value checkbox filters
    for (const [key, label] of Object.entries(labels)) {
      const val = this._filters[key];
      if (!val) continue;
      for (const v of val.split(',').filter(Boolean)) {
        chips.push({ key, value: v, label: `${label}: ${v}` });
      }
    }

    // Price range
    if (this._filters.min_price) {
      chips.push({ key: 'min_price', value: null, label: `Pris fra ${this._filters.min_price} kr` });
    }
    if (this._filters.max_price) {
      chips.push({ key: 'max_price', value: null, label: `Pris til ${this._filters.max_price} kr` });
    }

    // Stock
    if (this._filters.in_stock) {
      chips.push({ key: 'in_stock', value: null, label: 'På lager' });
    }

    if (chips.length === 0) {
      this._activeFiltersEl.innerHTML = '';
      return;
    }

    this._activeFiltersEl.innerHTML = chips.map((c, i) =>
      `<span class="as__chip">
        <span class="as__chip-label">${this._esc(c.label)}</span>
        <button type="button" class="as__chip-x" data-chip-idx="${i}" aria-label="Fjern">&times;</button>
      </span>`
    ).join('');

    // Bind remove clicks
    for (const btn of this._activeFiltersEl.querySelectorAll('.as__chip-x')) {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.chipIdx, 10);
        const chip = chips[idx];
        if (!chip) return;
        this._removeChipFilter(chip);
      });
    }
  }

  _removeChipFilter(chip) {
    const { key, value } = chip;

    if (key === 'in_stock') {
      delete this._filters.in_stock;
      if (this._stockCheckbox) this._stockCheckbox.checked = false;
    } else if (key === 'min_price') {
      delete this._filters.min_price;
      this._priceMin = this._priceAbsMin;
      if (typeof this._updatePriceSliderUI === 'function') this._updatePriceSliderUI();
    } else if (key === 'max_price') {
      delete this._filters.max_price;
      this._priceMax = this._priceAbsMax;
      if (typeof this._updatePriceSliderUI === 'function') this._updatePriceSliderUI();
    } else if (value) {
      // Multi-value: remove single value from comma-separated list
      const current = (this._filters[key] || '').split(',').filter(Boolean);
      const updated = current.filter(v => v !== value);
      if (updated.length > 0) {
        this._filters[key] = updated.join(',');
      } else {
        delete this._filters[key];
      }
      // Uncheck the matching checkbox in the accordion
      const acc = this._accordions[key];
      if (acc) {
        const cb = acc.querySelector(`input[type="checkbox"][value="${CSS.escape(value)}"]`);
        if (cb) cb.checked = false;
      }
    }

    this._page = 1;
    this._updateFilterCount();
    this._renderActiveFilters();
    this._fetchResults();
  }

  _resetFilters() {
    sessionStorage.removeItem(this._stateKey);
    this._filters = {};
    this._page = 1;

    // Uncheck all checkboxes
    for (const acc of Object.values(this._accordions)) {
      for (const cb of acc.querySelectorAll('input[type="checkbox"]')) {
        cb.checked = false;
      }
    }

    // Reset stock checkbox
    if (this._stockCheckbox) this._stockCheckbox.checked = false;

    // Reset price slider
    this._priceMin = this._priceAbsMin;
    this._priceMax = this._priceAbsMax;
    if (typeof this._updatePriceSliderUI === 'function') {
      this._updatePriceSliderUI();
    }

    // Reset sort
    this._sort = '';
    if (this._sortSelect) this._sortSelect.value = '';

    // Reset search input
    this._query = '';
    if (this._searchInput) this._searchInput.value = '';

    this._updateFilterCount();
    this._renderActiveFilters();
    this._fetchResults();
  }

  // ── Shared utilities ──────────────────────────────────────────────────

  _esc(str) {
    const d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  }
}

if (!customElements.get('accessory-search')) {
  customElements.define('accessory-search', AccessorySearchElement);
}
